import React, { useState } from 'react';
import { X } from 'lucide-react';
import {
  FORMAT_ADAPTATION_OPTIONS,
  FORMAT_BALANCE_OPTIONS,
  FORMAT_CONSTRAINT_OPTIONS,
  FORMAT_DIALOGUE_STYLE_OPTIONS,
  FORMAT_DURATION_OPTIONS,
  FORMAT_FIELD_OPTIONS,
  FORMAT_GENRE_OPTIONS,
  FORMAT_LANGUAGE_OPTIONS,
  FORMAT_PERSPECTIVE_OPTIONS,
  FORMAT_SUBGENRE_OPTIONS,
  FORMAT_TONE_OPTIONS,
  FORMAT_VISUAL_OPTIONS,
  STORY_AUDIENCE_OPTIONS,
  optionsIncludingCurrent,
} from '@/lib/unifiedStoryPipeline';

const ROWS = [
  ['format', 'Format', 'text', FORMAT_FIELD_OPTIONS],
  ['genres', 'Genres', 'list', FORMAT_GENRE_OPTIONS],
  ['subgenres', 'Subgenres', 'list', FORMAT_SUBGENRE_OPTIONS],
  ['tone', 'Tone', 'text', FORMAT_TONE_OPTIONS],
  ['audience', 'Audience', 'text', STORY_AUDIENCE_OPTIONS],
  ['duration_label', 'Approximate duration', 'duration', FORMAT_DURATION_OPTIONS],
  ['narrative_perspective', 'Narrative perspective', 'text', FORMAT_PERSPECTIVE_OPTIONS],
  ['balance', 'Dialogue and narration', 'text', FORMAT_BALANCE_OPTIONS],
  ['visual_treatment', 'Visual treatment', 'text', FORMAT_VISUAL_OPTIONS],
  ['language', 'Language', 'text', FORMAT_LANGUAGE_OPTIONS],
  ['dialogue_style', 'Dialogue style', 'text', FORMAT_DIALOGUE_STYLE_OPTIONS],
  ['adaptation', 'Faithful or creative adaptation', 'text', FORMAT_ADAPTATION_OPTIONS],
  ['constraints', 'Constraints', 'text', FORMAT_CONSTRAINT_OPTIONS],
];

function displayValue(value) {
  if (Array.isArray(value)) return value.join(', ');
  if (value === 0 || value) return String(value);
  return '';
}

const STORYTELLING_CODES = ['dialogue', 'narrator_and_dialogue', 'narrator'];

const FORMAT_BY_CODE = {
  dialogue: 'dialogue',
  narrator_and_dialogue: 'dialogue with narration',
  narrator: 'narration',
};

const BALANCE_BY_CODE = {
  dialogue: 'dialogue',
  narrator_and_dialogue: 'mixed',
  narrator: 'narration',
};

function storytellingChoices(options) {
  const byValue = new Map((options || []).map((option) => [String(option.value), option]));
  if (!STORYTELLING_CODES.every((value) => byValue.has(value))) return [];
  return STORYTELLING_CODES.map((value) => byValue.get(value));
}

function choiceMatchesFormat(option, format) {
  const current = String(format || '').trim().toLowerCase();
  if (!current) return false;
  return current === FORMAT_BY_CODE[option.value] || current === String(option.label || '').trim().toLowerCase();
}

function payloadFrom(draft) {
  const recommendation = {};
  ROWS.forEach(([key, , kind]) => {
    const raw = draft[key] ?? '';
    if (kind === 'list') {
      recommendation[key] = String(raw).split(',').map((part) => part.trim()).filter(Boolean);
    } else {
      recommendation[key] = raw;
    }
  });
  if (draft.duration_seconds) recommendation.duration_seconds = Number(draft.duration_seconds) || 0;
  return recommendation;
}

export function StoryFormatReview({ storyFormat, storytellingOptions, staleNotice, busy, onConfirm, onEdit }) {
  const recommendation = storyFormat?.recommendation || {};
  const unknown = new Set(storyFormat?.unknown || recommendation.unknown || []);
  const [draft, setDraft] = useState(() => Object.fromEntries(
    ROWS.map(([key]) => [key, displayValue(recommendation[key])])
  ));
  const [seconds, setSeconds] = useState(recommendation.duration_seconds || '');
  const locked = storyFormat?.status === 'locked';
  const notice = storyFormat?.fallback_notice || '';
  const [open, setOpen] = useState(true);

  const submit = (action) => {
    const next = payloadFrom({ ...draft, duration_seconds: seconds });
    if (action === 'edit') onEdit?.(next);
    else onConfirm?.(next);
  };

  const choose = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    if (key === 'duration_label') {
      const match = FORMAT_DURATION_OPTIONS.find((option) => option.value === value);
      if (match) setSeconds(match.seconds);
    }
  };

  const summary = [draft.format, draft.tone, draft.audience, draft.duration_label].filter(Boolean).join(' · ');
  const samples = storytellingChoices(storytellingOptions);
  const incomplete = ROWS.some(([key]) => !String(draft[key] ?? '').trim());

  const pickStorytelling = (option) => {
    setDraft((prev) => ({
      ...prev,
      format: FORMAT_BY_CODE[option.value] || prev.format,
      balance: BALANCE_BY_CODE[option.value] || prev.balance,
    }));
  };

  const fields = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {ROWS.filter(([key]) => !(samples.length && key === 'format')).map(([key, label, , options]) => {
        const needsDecision = unknown.has(key) || (key === 'duration_label' && unknown.has('duration_seconds'));
        const choices = optionsIncludingCurrent(options, draft[key]);
        return (
          <label key={key} className="block text-[10px] text-[#9CA3AF]" data-unknown={needsDecision ? key : undefined}>
            <span className="flex items-center justify-between gap-2">
              {label}
              {needsDecision ? <span className="text-amber-200">Needs your decision</span> : null}
            </span>
            <select
              data-testid={key === 'format' ? 'story-format-custom' : `story-format-${key}`}
              value={draft[key] ?? ''}
              onChange={(event) => choose(key, event.target.value)}
              className={`mt-1 w-full bg-[#0c1018] border rounded-lg px-2 py-1.5 text-xs text-white ${needsDecision ? 'border-amber-300/50' : 'border-white/10'}`}
            >
              <option value="">Choose…</option>
              {choices.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        );
      })}
    </div>
  );

  const confirmButton = locked ? (
    <button
      type="button"
      disabled={Boolean(busy)}
      onClick={() => submit('edit')}
      className="w-full py-2.5 rounded-xl border border-white/15 text-xs text-white cursor-pointer disabled:opacity-60"
      data-testid="story-format-edit"
    >
      Change storytelling format
    </button>
  ) : (
    <button
      type="button"
      disabled={Boolean(busy) || incomplete}
      onClick={() => submit('confirm')}
      className="w-full py-2.5 rounded-xl bg-[#2DD4BF] text-[#06080D] text-xs font-bold cursor-pointer disabled:opacity-60"
      data-testid="story-format-confirm"
    >
      Confirm storytelling format
    </button>
  );

  return (
    <div className="space-y-2" data-testid="story-format-review">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">Storytelling format</p>
        <h3 className="text-sm font-semibold text-white">How would you like this story to be told?</h3>
        {summary ? <p className="text-xs text-[#E5E7EB]">{summary}</p> : null}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[11px] text-[#5EEAD4] underline cursor-pointer"
        >
          Review the choices
        </button>
      </div>
      {open ? (
        <div
          className="fixed inset-0 z-[240] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Storytelling format"
            onClick={(event) => event.stopPropagation()}
            className={`w-full ${samples.length ? 'max-w-3xl' : 'max-w-xl'} max-h-[85vh] overflow-hidden flex flex-col rounded-2xl border border-white/15 bg-[#0c1018] shadow-2xl`}
          >
            <div className="flex items-start justify-between gap-3 p-4 border-b border-white/10">
              <div>
                <h3 className="text-sm font-semibold text-white">How would you like this story to be told?</h3>
                <p className="text-[11px] text-[#9CA3AF] mt-1">
                  {samples.length
                    ? 'Compare a sample scene for dialogue, dialogue with narration, and narration, then pick one.'
                    : 'Pick each line. The script, voices, and character views come after this.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full border border-white/10 text-[#9CA3AF] hover:text-white flex items-center justify-center cursor-pointer"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {storyFormat?.format_id ? (
                <p className="text-[10px] text-[#6B7280]">
                  Format <span data-testid="story-format-id">{storyFormat.format_id}</span>
                  {storyFormat.built_from_context_version_id ? ` · from context ${storyFormat.built_from_context_version_id}` : ''}
                </p>
              ) : null}
              {incomplete ? (
                <p className="text-[11px] text-[#9CA3AF]" data-testid="format-contract-incomplete">
                  Choose every line. A blank line was not in the format contract, so it was left for you.
                </p>
              ) : null}
              {notice ? (
                <p className="rounded-xl border border-amber-400/40 bg-amber-950/40 p-3 text-xs text-amber-100" data-testid="format-fallback-notice">
                  {notice}
                </p>
              ) : null}
              {staleNotice || storyFormat?.status === 'stale' ? (
                <p className="rounded-xl border border-amber-400/40 bg-amber-950/40 p-3 text-xs text-amber-100" data-testid="format-stale-notice">
                  {staleNotice || 'The context changed. Confirm how the story should be told again.'}
                </p>
              ) : null}
              {samples.length ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="story-format-samples">
                  {samples.map((option) => {
                    const selected = choiceMatchesFormat(option, draft.format);
                    const sample = String(option.sample || '').trim();
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => pickStorytelling(option)}
                        data-testid={`story-format-choice-${option.value}`}
                        className={`text-left rounded-xl border p-3 cursor-pointer ${
                          selected
                            ? 'border-[#2DD4BF] bg-[#2DD4BF]/10'
                            : 'border-white/10 bg-white/[0.03] hover:border-[#2DD4BF]/40'
                        }`}
                      >
                        <span className="text-xs font-semibold text-white">
                          {option.label}
                          {option.recommended ? (
                            <span className="ml-1.5 text-[9px] uppercase tracking-wide text-[#5EEAD4]">suggested</span>
                          ) : null}
                          {selected ? (
                            <span className="ml-1.5 text-[9px] uppercase tracking-wide text-[#2DD4BF]">selected</span>
                          ) : null}
                        </span>
                        {sample ? (
                          <pre
                            data-testid="story-format-sample"
                            className="mt-2 whitespace-pre-wrap font-sans text-[11px] leading-relaxed text-[#D1D5DB] max-h-40 overflow-y-auto"
                          >
                            {sample}
                          </pre>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {fields}
            </div>
            <div className="p-4 border-t border-white/10">
              {confirmButton}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
